/**
 * `SitesList` — ported from `Foyer_Dashboard_CURRENT.jsx` line 839.
 *
 * Phase 1.5: the per-site overflow menu's "Remove" action is now enabled
 * (founder cleanup of test-site clutter). The confirm dialog lives on
 * the `SiteCard` itself so each card owns its own dialog state; this
 * view wires the `onDeleted(site_id)` callback to converge the local
 * sites array with the server (refresh + clear the active-site pointer
 * if the deleted site was active).
 */

import { Plus } from 'lucide-react';

import { useSites } from '../../app/providers.js';
import type { View } from '../../app/view-router.js';
import { useC } from '../../lib/theme.js';
import { NumLabel } from '../../components/atoms/index.js';
import { AccountUsageStrip } from './AccountUsageStrip.js';
import { SetupGuideCard } from './SetupGuideCard.js';
import { SitesListSkeleton } from './SitesListSkeleton.js';
import { SkillsGapNudge } from './SkillsGapNudge.js';
import { SiteCard } from './SiteCard.js';

const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface SitesListProps {
  setView: (v: View) => void;
}

export function SitesList({ setView }: SitesListProps): JSX.Element {
  const C = useC();
  const { sites, activeSite, setActiveSite, refresh, loaded } = useSites();

  // Founder 2026-10-20: hold the skeleton until the first fetch
  // settles. The previous render always painted "Add your first site"
  // for a tick before the real list arrived — a false "no sites"
  // flash. Treat an absent `loaded` (test provider back-compat) as
  // already-loaded so existing tests keep rendering unchanged.
  if (loaded === false && sites.length === 0) {
    return <SitesListSkeleton />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex items-end justify-between mb-8 sm:mb-10 flex-wrap gap-4">
        <div>
          <NumLabel n={0}>your sites · {sites.length} total</NumLabel>
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(36px, 6vw, 56px)',
              lineHeight: 1,
              color: C.text,
              letterSpacing: -1.5,
            }}
          >
            All your sites,
            <br />
            <em>one front door</em>.
          </h1>
        </div>
        <button
          type="button"
          onClick={() => {
            // Fresh add — clear any pending site that would otherwise
            // put Onboarding into resume mode (step 1) and lock the
            // domain field. The `'addSite'` view aliases Onboarding
            // per ADR-0025 §2.1.
            setActiveSite(null);
            setView('addSite');
          }}
          className="flex items-center gap-2 px-4 py-2.5"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          <Plus size={14} /> Add site
        </button>
      </div>

      {/* ADR-0167 §3 — three-milestone setup guide. Derives from server
          truth already in the client, targets the most-progressed site,
          hides itself when any site is fully live or after dismissal. */}
      <SetupGuideCard setView={setView} />

      {/* 2026-07-09 redesign — split from SetupGuideCard: a live,
          recurring "skills enabled but not backed" signal, not a
          one-time checklist. Never dismissible; see its own docblock. */}
      <SkillsGapNudge setView={setView} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sites.map((s) => (
          <SiteCard
            key={s.site_id}
            site={s}
            onClick={() => {
              setActiveSite(s);
              // Pending sites route into Onboarding, which resumes at
              // the install + verify step because `activeSite.status` is
              // `pending_proof` (the DB-side enum that maps to the UI's
              // `pending_verification` bucket per ADR-0043 §4.1 — the
              // legacy CNAME / verification-file remedies were retired;
              // the remedies the merchant actually has are
              // snippet-presence / `.well-known` / Shopify OAuth proof).
              // Verified-but-no-ping ("awaiting_install") also routes
              // into Onboarding; resume at the install step is handled
              // by `Onboarding`'s initialStep logic. Only fully-live
              // sites jump straight to mandates.
              const live = (s.status === 'discovery_active'
                            || s.status === 'transactional_active')
                           && !!s.last_seen_at;
              // Founder 2026-09-23: a live site opens on the Dashboard, not the Mandates list.
              setView(live ? 'dashboard' : 'onboarding');
            }}
            onSettings={() => {
              setActiveSite(s);
              setView('settings');
            }}
            onReviewOrigins={() => {
              // ADR-0057 §5 — the SiteCard's UNREGISTERED ORIGINS
              // band lands the merchant on this site's Origins
              // settings tab via a `?settings_tab=origins` deep-link
              // query param. The Settings shell reads it on mount
              // and clears it after consumption so a manual tab
              // change isn't overwritten on the next remount.
              setActiveSite(s);
              if (typeof window !== 'undefined') {
                try {
                  const url = new URL(window.location.href);
                  url.searchParams.set('settings_tab', 'origins');
                  window.history.replaceState({}, '', url.toString());
                } catch {
                  // Best-effort; the Settings shell defaults to the
                  // General tab on parse failure.
                }
              }
              setView('settings');
            }}
            onInstallUpdate={() => {
              // Founder 2026-10-27 — the update band lands on Settings ->
              // Install, scrolled to the "Update available" steps.
              setActiveSite(s);
              if (typeof window !== 'undefined') {
                try {
                  const url = new URL(window.location.href);
                  url.searchParams.set('settings_tab', 'install');
                  url.searchParams.set('settings_section', 'install-update');
                  window.history.replaceState({}, '', url.toString());
                } catch {
                  // Best-effort; Settings opens on its default tab.
                }
              }
              setView('settings');
            }}
            onInstall={() => {
              setActiveSite(s);
              setView('onboarding');
            }}
            onDeleted={(deleted_id) => {
              // If the user just deleted the active site, clear the
              // pointer so downstream views (TopBar switcher, settings)
              // don't render against a ghost row. `refresh()` re-reads
              // the server's authoritative list — the server now hides
              // the soft-deleted row, so the local array converges.
              if (activeSite?.site_id === deleted_id) {
                setActiveSite(null);
              }
              void refresh();
            }}
          />
        ))}
        <button
          type="button"
          onClick={() => {
            setActiveSite(null);
            setView('addSite');
          }}
          className="p-6 flex flex-col items-center justify-center gap-3 text-center transition-all hover:opacity-80"
          style={{
            background: 'transparent',
            border: `1.5px dashed ${C.border}`,
            borderRadius: 4,
            minHeight: 240,
          }}
        >
          <div
            className="flex items-center justify-center"
            style={{
              width: 44,
              height: 44,
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 4,
            }}
          >
            <Plus size={20} color={C.muted} />
          </div>
          <div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 14,
                color: C.text,
                fontWeight: 500,
                marginBottom: 4,
              }}
            >
              {sites.length === 0 ? 'Add your first site' : 'Add another site'}
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.5,
                maxWidth: 220,
              }}
            >
              One install per domain. Settings, mandates, and billing roll up here.
            </div>
          </div>
        </button>
      </div>

      <AccountUsageStrip />
    </div>
  );
}
